import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Trash2, Store, AlertCircle,
  Search, Check, ChevronDown, X, Loader2,
} from "lucide-react";
import { listStock, createInventoryAdjustment } from "../../api/inventory";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Button, Field, Input, Textarea } from "../../components/ui";
import { unwrapList, errorMessage, fieldErrors } from "../../lib/records";
import toast from "react-hot-toast";

const ITEM_PAGE_SIZE = 30;

// A searchable, scroll-paginated item picker. Fetches 30 items at a time from
// the stock endpoint; loads the next page as the user scrolls near the bottom.
function ItemPicker({ branchId, isChain, value, onChange, disabled, error }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const dropRef = useRef(null);
  const listRef = useRef(null);
  const sentinel = useRef(null);        // invisible div at list bottom
  const searchTimeout = useRef(null);

  // The currently selected item (for the button label)
  const [selectedItem, setSelectedItem] = useState(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const fetchPage = useCallback(async (newSearch, newPage) => {
    const isFirst = newPage === 1;
    if (isFirst) setLoading(true); else setLoadingMore(true);
    try {
      const res = await listStock({
        page: newPage,
        limit: ITEM_PAGE_SIZE,
        sort: "name",
        ...(newSearch.trim() ? { search: newSearch.trim() } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
      });
      const { items: rows, pagination } = unwrapList(res, ITEM_PAGE_SIZE);
      // Map to the shape we need: itemId = the catalogue ObjectId in row.item
      const mapped = rows.map((row) => ({
        itemId: typeof row.item === "string" ? row.item : (row.item?._id ?? row._id),
        label: row.itemName ?? "—",
        version: row.version,
        currentQty: row.quantity,
        unit: row.unit ?? "",
      }));
      setItems((prev) => isFirst ? mapped : [...prev, ...mapped]);
      setHasMore(newPage < (pagination.pages ?? 1));
    } catch {
      if (isFirst) setItems([]);
    } finally {
      if (isFirst) setLoading(false); else setLoadingMore(false);
    }
  }, [branchId, isChain]);

  // First page on open or when branch changes
  useEffect(() => {
    if (!open) return;
    setSearch("");
    setPage(1);
    fetchPage("", 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId]);

  // Debounced search
  function handleSearch(q) {
    setSearch(q);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      fetchPage(q, 1);
    }, 300);
  }

  // IntersectionObserver — load next page when the sentinel enters view
  useEffect(() => {
    if (!open || !sentinel.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          const next = page + 1;
          setPage(next);
          fetchPage(search, next);
        }
      },
      { root: listRef.current, threshold: 0.1 }
    );
    obs.observe(sentinel.current);
    return () => obs.disconnect();
  }, [open, hasMore, loadingMore, page, search, fetchPage]);

  function selectItem(opt) {
    setSelectedItem(opt);
    onChange({ itemId: opt.itemId, version: opt.version });
    setOpen(false);
  }

  function clearSelection(e) {
    e.stopPropagation();
    setSelectedItem(null);
    onChange({ itemId: "", version: undefined });
  }

  const btnBase = `w-full flex items-center justify-between rounded border px-3.5 py-2.5 text-left text-sm transition bg-surface-container-lowest ${
    error
      ? "border-error focus:border-error"
      : open
        ? "border-emerald-500 shadow-glow"
        : "border-outline-variant hover:border-emerald-300"
  }`;

  return (
    <div className="relative" ref={dropRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={btnBase}
      >
        {selectedItem ? (
          <span className="flex items-center gap-2 min-w-0">
            <span className="font-medium text-gray-800 truncate">{selectedItem.label}</span>
            <span className="shrink-0 text-xs text-gray-400 tabular-nums">
              {selectedItem.currentQty} {selectedItem.unit}
            </span>
          </span>
        ) : (
          <span className="text-gray-400">
            {disabled ? "Select a branch first…" : "Pick an item…"}
          </span>
        )}
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {selectedItem && (
            <span
              role="button"
              tabIndex={0}
              onClick={clearSelection}
              onKeyDown={(e) => e.key === "Enter" && clearSelection(e)}
              className="rounded p-0.5 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer"
              title="Clear"
            >
              <X size={13} />
            </span>
          )}
          <ChevronDown size={15} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-50 mt-1.5 flex flex-col rounded-lg border border-emerald-100 bg-white shadow-level3">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-emerald-100 px-3 py-2">
            <Search size={14} className="shrink-0 text-gray-400" />
            <input
              autoFocus
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search items…"
              className="flex-1 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 outline-none"
            />
            {loading && <Loader2 size={13} className="animate-spin text-emerald-500 shrink-0" />}
          </div>

          {/* Scrollable list */}
          <div
            ref={listRef}
            className="max-h-56 overflow-y-auto overscroll-contain"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400">
                <Loader2 size={15} className="animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-400">No stock items found.</p>
            ) : (
              <>
                {items.map((opt) => {
                  const isSelected = value === opt.itemId;
                  return (
                    <button
                      key={opt.itemId}
                      type="button"
                      onClick={() => selectItem(opt)}
                      className={`flex w-full items-center justify-between gap-3 px-3.5 py-2.5 text-left text-sm transition-colors hover:bg-emerald-50 ${
                        isSelected ? "bg-emerald-50/80 text-emerald-800" : "text-gray-800"
                      }`}
                    >
                      <span className="flex-1 truncate font-medium">{opt.label}</span>
                      <span className="shrink-0 tabular-nums text-xs text-gray-400">
                        {opt.currentQty} {opt.unit}
                      </span>
                      {isSelected && <Check size={14} className="shrink-0 text-emerald-600" />}
                    </button>
                  );
                })}

                {/* Sentinel div — IntersectionObserver triggers next page fetch */}
                <div ref={sentinel} className="h-1" />

                {loadingMore && (
                  <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-400">
                    <Loader2 size={12} className="animate-spin" /> Loading more…
                  </div>
                )}
                {!hasMore && items.length > 0 && (
                  <p className="py-2 text-center text-xs text-gray-400">
                    All {items.length} item{items.length !== 1 ? "s" : ""} shown
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------

export default function InventoryAdjustPage() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";

  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState("");
  const [fieldErrs, setFieldErrs] = useState({});
  const [saving, setSaving] = useState(false);

  // Each line: { itemId, quantity, note, version }
  const [lines, setLines] = useState([{ itemId: "", quantity: "", note: "", version: undefined }]);

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((r) => setBranches(unwrapList(r, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  // Reset lines when branch changes so stale versions don't cause 409s.
  useEffect(() => {
    setLines([{ itemId: "", quantity: "", note: "", version: undefined }]);
  }, [branchId]);

  function addLine() {
    setLines((prev) => [...prev, { itemId: "", quantity: "", note: "", version: undefined }]);
  }

  function removeLine(idx) {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  }

  function updateLine(idx, field, value) {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }

  function setLineItem(idx, { itemId, version }) {
    setLines((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], itemId, version };
      return next;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    setFieldErrs({});

    const validLines = lines.filter((l) => l.itemId && l.quantity !== "");
    if (validLines.length === 0) {
      setFormError("Add at least one item line before submitting.");
      return;
    }
    if (isChain && !branchId) {
      setFormError("Select a branch for this adjustment.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...(isChain && branchId ? { partner: branchId } : {}),
        lines: validLines.map((l) => ({
          item: l.itemId,
          quantity: Number(l.quantity),
          ...(l.version != null ? { expectedVersion: l.version } : {}),
          ...(l.note.trim() ? { note: l.note.trim() } : {}),
        })),
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      await createInventoryAdjustment(payload);
      toast.success("Adjustment submitted.");
      setLines([{ itemId: "", quantity: "", note: "", version: undefined }]);
      setNote("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setFormError(
          "A conflict was detected — someone else updated the stock since you opened this page. Reload and try again."
        );
      } else {
        const mapped = fieldErrors(err);
        if (mapped) setFieldErrs(mapped);
        else setFormError(errorMessage(err, "The adjustment could not be saved."));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8 max-w-3xl mx-auto">
        <Link to="/inventory" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors">
          <ArrowLeft size={15} /> Back to stock
        </Link>

        <PageHeader
          title="Adjust stock"
          subtitle="Correct what the system shows against what is actually on the shelf."
        />

        <Card className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 p-4 sm:p-6">
          <div className="mb-5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>
              Quantities you enter <strong>replace</strong> the current balance — they do not add to it.
              Enter the actual count you see on the shelf right now.
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {formError && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}

            {/* Branch picker — chain only */}
            {isChain && (
              <Field label="Branch" required error={fieldErrs.partner}>
                <div className="flex items-center gap-2">
                  <Store size={15} className="shrink-0 text-gray-400" />
                  <select
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                    required
                    className="flex-1 w-full rounded border border-outline-variant bg-surface-container-lowest px-3.5 py-2.5 text-body-md text-on-surface outline-none transition focus:border-secondary focus:shadow-glow text-sm"
                  >
                    <option value="">Select a branch…</option>
                    {branches.map((b) => (
                      <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                  </select>
                </div>
              </Field>
            )}

            {/* Item lines */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-label-md text-on-surface font-medium">Items to adjust</span>
                <span className="text-xs text-gray-500">{lines.length} line{lines.length !== 1 ? "s" : ""}</span>
              </div>

              <div className="space-y-3">
                {lines.map((line, idx) => (
                  <div key={idx} className="flex gap-2 items-start rounded-lg border border-gray-100 bg-gray-50/60 p-3">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">

                      {/* Item picker — searchable, paginated 30/page */}
                      <div className="sm:col-span-2">
                        <ItemPicker
                          branchId={branchId}
                          isChain={isChain}
                          value={line.itemId}
                          onChange={(opt) => setLineItem(idx, opt)}
                          disabled={isChain && !branchId}
                          error={fieldErrs[`lines.${idx}.item`]}
                        />
                        {fieldErrs[`lines.${idx}.item`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrs[`lines.${idx}.item`]}</p>
                        )}
                      </div>

                      {/* New quantity */}
                      <div>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, "quantity", e.target.value)}
                          placeholder="New qty"
                          className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                        />
                        {fieldErrs[`lines.${idx}.quantity`] && (
                          <p className="mt-1 text-xs text-red-600">{fieldErrs[`lines.${idx}.quantity`]}</p>
                        )}
                      </div>

                      {/* Line note */}
                      <div className="sm:col-span-3">
                        <Input
                          value={line.note}
                          onChange={(e) => updateLine(idx, "note", e.target.value)}
                          placeholder="Note for this item (optional)"
                          className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                        />
                      </div>
                    </div>

                    {lines.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        className="mt-0.5 rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                        title="Remove line"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addLine}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors"
              >
                <Plus size={15} /> Add another item
              </button>
            </div>

            {/* Overall note */}
            <Field label="Overall note" error={fieldErrs.note}>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="Why is this adjustment being made? (optional)"
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </Field>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
              <Link to="/inventory">
                <Button type="button" variant="ghost" className="text-gray-600 hover:bg-gray-100 w-full sm:w-auto">
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                loading={saving}
                className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
              >
                Submit adjustment
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
